// Client API du module questionnaires.
// Mono-domaine (plan Asten §4-E) : same-origin par défaut, comme services/api.js.
// Les chemins ci-dessous sont donc relatifs (/api/surveys/…) et c'est Traefik (prod)
// ou nginx / setupProxy.js (dev) qui route vers le service forms.
// REACT_APP_SURVEY_API_URL reste disponible pour cibler un backend distant en dev
// (ex. 'http://localhost:8001' pour attaquer directement le conteneur forms_api).
export const SURVEY_API_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SURVEY_API_URL) || '';

async function handle(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail;
    if (Array.isArray(detail)) {
      throw new Error(detail.map((d) => `${(d.loc || []).slice(-1)}: ${d.msg}`).join(' · '));
    }
    throw new Error(detail || `Erreur ${res.status}`);
  }
  return res.json();
}

// Groupe de passation et vague en cours d'une répondante, à partir de son code.
// Premier appel du wizard : il faut savoir de quel calendrier relève la personne
// avant de demander le bon schéma (les groupes sont décalés dans le temps).
//
// Le code voyage dans le CORPS et non dans l'URL : une query string finit dans
// les journaux d'accès (Traefik, nginx), dans le `Referer` et dans les clés de
// cache. C'est le même réflexe que le lien d'activation de la console chercheur,
// qui passe par le fragment d'URL.
//
// Ce que le client déclare, c'est QUI il est — jamais où en est l'étude : la
// phase reste décidée côté serveur.
export async function resolveSurveySession(participantCode) {
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ participant_code: participantCode }),
  });
  return handle(res);
}

// Auto-inscription : la participante crée son code depuis le lien de son groupe
// (?groupe=g2). Le serveur refuse (409) si le groupe n'est plus en phase amont ou
// si le code est déjà pris. Renvoie le calendrier, comme `resolveSurveySession`.
export async function registerSurveyCode(participantCode, cohort) {
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ participant_code: participantCode, cohort }),
  });
  return handle(res);
}

// Phase en cours d'un groupe (public). Le wizard ne propose de créer un code que
// pendant la phase amont de son groupe ; hors de cette fenêtre, le serveur refuse
// toute inscription, l'écran n'a donc pas à la proposer.
export async function fetchCohortPhase(slug) {
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/cohorts/${encodeURIComponent(slug)}/phase`);
  return handle(res);
}

// Schéma JSON du questionnaire (source unique côté backend -> pas de désync).
// Le backend le réduit aux seules questions posées pendant la vague en cours du
// groupe demandé : le wizard n'a donc rien à filtrer lui-même. Sans `cohort`,
// c'est la vague du groupe par défaut.
export async function fetchSurveySchema(form, cohort) {
  const query = cohort ? `?cohort=${encodeURIComponent(cohort)}` : '';
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/schema/${form}${query}`);
  return handle(res);
}

// Phase du groupe par défaut (amont / 1-4 / fermee). `is_open` = une vague est
// ouverte. Vue d'ensemble : une répondante passe par `resolveSurveySession`.
export async function fetchCurrentPhase() {
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/phase`);
  return handle(res);
}

// Liste des groupes de passation et de leur vague en cours. Public.
export async function fetchSurveyCohorts() {
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/cohorts`);
  return handle(res);
}

// Soumission finale. Le corps porte `participant_code` (pseudonyme, permet
// d'apparier les vagues et d'honorer un droit de retrait) ; la phase et le groupe,
// eux, sont estampillés par le serveur — qui relit le registre à partir du code —
// et ne sont jamais envoyés d'ici.
export async function submitSurvey(form, payload) {
  const res = await fetch(`${SURVEY_API_URL}/api/surveys/${form}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle(res);
}
